import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import config from '../Common_Files/Config';
import Swal from 'sweetalert2';

const parseMoney = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const n = parseFloat(String(v).replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : 0;
};

const getPoLabel = (row) => {
    const ref = String(row?.reference_no || '').trim();
    if (ref) return ref;
    const id = String(row?.purchase_id || '').trim();
    if (!id) return '';
    return id.toUpperCase().startsWith('PO') ? id : `PO${id}`;
};

const swalAboveModal = (options) =>
    Swal.fire({
        ...options,
        customClass: {
            ...(options.customClass || {}),
            container: 'supplier-credit-swal-on-top',
        },
    });

const SupplierCreditAllocation = ({
    supplierId,
    supplierName,
    initialCreditLedgerId,
    onClose,
    onSuccess,
}) => {
    const accessToken = localStorage.getItem('accessToken');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [credits, setCredits] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [selectedCreditId, setSelectedCreditId] = useState('');
    const [checkedInvoices, setCheckedInvoices] = useState({});
    const [allocAmounts, setAllocAmounts] = useState({});
    const [formError, setFormError] = useState('');

    const selectedCredit = useMemo(
        () => credits.find((c) => String(c.credit_ledger_id) === String(selectedCreditId)),
        [credits, selectedCreditId]
    );

    const creditAvailable = parseMoney(selectedCredit?.balance);

    const totalSelected = useMemo(() => {
        let sum = 0;
        Object.keys(checkedInvoices).forEach((key) => {
            if (!checkedInvoices[key]) return;
            sum += parseMoney(allocAmounts[key]);
        });
        return Math.round(sum * 100) / 100;
    }, [checkedInvoices, allocAmounts]);

    const getRemainingCredit = useCallback(
        (amounts, checked, excludeKey) => {
            let used = 0;
            Object.keys(checked).forEach((k) => {
                if (!checked[k] || (excludeKey && k === excludeKey)) return;
                used += parseMoney(amounts[k]);
            });
            return Math.max(0, Math.round((creditAvailable - used) * 100) / 100);
        },
        [creditAvailable]
    );

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.body.classList.add('supplier-credit-modal-open');
        return () => {
            document.body.style.overflow = prev;
            document.body.classList.remove('supplier-credit-modal-open');
        };
    }, []);

    const resolveDefaultCreditId = (creditList, preferredId) => {
        if (!creditList.length) return '';
        if (preferredId) {
            const match = creditList.find(
                (c) => String(c.credit_ledger_id) === String(preferredId)
            );
            if (match) return String(match.credit_ledger_id);
        }
        return String(creditList[0].credit_ledger_id);
    };

    const loadData = async () => {
        if (!supplierId || !accessToken) return;
        setLoading(true);
        setFormError('');
        const formData = new FormData();
        formData.append('supplier_id', supplierId);
        try {
            const response = await fetch(`${config.api_url}/ws-supplier-credit-allocation`, {
                method: 'POST',
                headers: { authorization: `Bearer ${accessToken}` },
                body: formData,
            });
            const json = await response.json();
            if (!json.success || !json.data) {
                onClose();
                swalAboveModal({
                    icon: 'error',
                    title: 'Error',
                    text: json.message || 'Failed to load allocation data',
                });
                return;
            }
            const creditList = json.data.credits || [];
            const invoiceList = json.data.invoices || [];
            setCredits(creditList);
            setInvoices(invoiceList);
            const defaultCredit = resolveDefaultCreditId(creditList, initialCreditLedgerId);
            setSelectedCreditId(defaultCredit);
            setCheckedInvoices({});
            setAllocAmounts({});
        } catch {
            onClose();
            swalAboveModal({ icon: 'error', title: 'Error', text: 'Failed to load allocation data' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [supplierId, initialCreditLedgerId]);

    const toggleInvoice = (ledgerId, balance) => {
        if (!selectedCreditId) {
            setFormError('Please select a credit note first.');
            return;
        }
        const key = String(ledgerId);
        setFormError('');
        setCheckedInvoices((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            setAllocAmounts((amts) => {
                if (!next[key]) {
                    const copy = { ...amts };
                    delete copy[key];
                    return copy;
                }
                const room = getRemainingCredit(amts, prev, key);
                const invBal = parseMoney(balance);
                const amt = Math.min(invBal, room);
                return { ...amts, [key]: amt > 0 ? amt.toFixed(2) : '' };
            });
            return next;
        });
    };

    const handleAmountChange = (ledgerId, value, maxBalance) => {
        const key = String(ledgerId);
        setFormError('');
        let num = parseMoney(value);
        const invMax = parseMoney(maxBalance);
        const creditMax = getRemainingCredit(allocAmounts, checkedInvoices, key);
        const max = Math.min(invMax, creditMax);
        if (num < 0) num = 0;
        if (num > max) num = max;
        setAllocAmounts((amts) => ({ ...amts, [key]: value === '' ? '' : String(num) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        if (!selectedCreditId) {
            setFormError('Please select a credit note.');
            return;
        }
        if (totalSelected <= 0) {
            setFormError('Select at least one invoice with an amount.');
            return;
        }
        if (totalSelected > creditAvailable + 0.009) {
            setFormError(
                `Total allocation (${totalSelected.toFixed(2)}) exceeds credit balance (${creditAvailable.toFixed(2)}). Reduce allocate amounts.`
            );
            return;
        }

        const allocations = [];
        Object.keys(checkedInvoices).forEach((key) => {
            if (!checkedInvoices[key]) return;
            const amt = parseMoney(allocAmounts[key]);
            if (amt > 0) {
                allocations.push({ purchase_ledger_id: parseInt(key, 10), amount: amt });
            }
        });

        if (allocations.length === 0) {
            setFormError('Enter allocation amounts for selected invoices.');
            return;
        }

        setSubmitting(true);
        const formData = new FormData();
        formData.append('supplier_id', supplierId);
        formData.append('credit_ledger_id', selectedCreditId);
        formData.append('allocations', JSON.stringify(allocations));

        try {
            const response = await fetch(`${config.api_url}/ws-submit-supplier-credit-allocation`, {
                method: 'POST',
                headers: { authorization: `Bearer ${accessToken}` },
                body: formData,
            });
            const json = await response.json();
            if (json.success) {
                onClose();
                if (onSuccess) onSuccess();
                swalAboveModal({
                    icon: 'success',
                    title: 'Allocated',
                    text: json.message || 'Credit allocated successfully.',
                    timer: 2000,
                    showConfirmButton: false,
                });
            } else {
                setFormError(json.message || 'Allocation failed.');
            }
        } catch {
            setFormError('Allocation failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const modalContent = (
        <div
            className="supplier-credit-allocation-backdrop"
            role="presentation"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="supplier-credit-allocation-dialog modal-content"
                role="dialog"
                aria-modal="true"
                aria-labelledby="supplier-credit-allocation-title"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="modal-header">
                    <h5 className="modal-title" id="supplier-credit-allocation-title">
                        Credit Note Allocation
                    </h5>
                    <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close" />
                </div>
                <form onSubmit={handleSubmit} className="supplier-credit-allocation-form">
                    <div className="supplier-credit-allocation-body modal-body">
                        {supplierName ? (
                            <p className="mb-2 fw-semibold">{supplierName}</p>
                        ) : null}
                        {loading ? (
                            <p className="text-center py-4 mb-0">Loading...</p>
                        ) : credits.length === 0 ? (
                            <p className="text-center py-4 mb-0">No unallocated credit notes for this supplier.</p>
                        ) : (
                            <>
                                <div className="supplier-credit-allocation-summary">
                                    <div className="row g-3 align-items-end">
                                        <div className="col-12 col-md-7">
                                            <label className="form-label" htmlFor="credit-note-select">
                                                Credit note
                                            </label>
                                            <select
                                                id="credit-note-select"
                                                className="form-select"
                                                value={selectedCreditId}
                                                onChange={(e) => {
                                                    setSelectedCreditId(e.target.value);
                                                    setCheckedInvoices({});
                                                    setAllocAmounts({});
                                                    setFormError('');
                                                }}
                                            >
                                                <option value="">Select credit note</option>
                                                {credits.map((c) => (
                                                    <option key={c.credit_ledger_id} value={c.credit_ledger_id}>
                                                        {c.bill_no || c.reference_no || `Credit #${c.credit_ledger_id}`}
                                                        {getPoLabel(c) ? ` (${getPoLabel(c)})` : ''} — Balance {c.balance}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <div className="small text-muted">Available</div>
                                            <div className="fs-5 fw-semibold">{creditAvailable.toFixed(2)}</div>
                                        </div>
                                        <div className="col-6 col-md-2">
                                            <div className="small text-muted">Allocating</div>
                                            <div className="fs-5 fw-semibold">{totalSelected.toFixed(2)}</div>
                                        </div>
                                    </div>
                                </div>
                                {formError ? (
                                    <div className="alert alert-warning py-2 mb-2" role="alert">
                                        {formError}
                                    </div>
                                ) : null}
                                {!selectedCreditId ? (
                                    <p className="text-muted mb-0">Select a credit note to see open invoices.</p>
                                ) : invoices.length === 0 ? (
                                    <p className="text-center py-3 mb-0">No open invoices to allocate.</p>
                                ) : (
                                    <>
                                        <p className="small text-muted mb-2">
                                            Tick invoices and enter amounts (max = credit available).
                                        </p>
                                        <div className="supplier-credit-allocation-table-wrap">
                                            <table className="table table-bordered table-sm mb-0">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '36px' }} />
                                                        <th>PO</th>
                                                        <th>Bill / Ref</th>
                                                        <th>Date</th>
                                                        <th className="text-end">Balance</th>
                                                        <th className="text-end" style={{ width: '130px' }}>Allocate</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoices.map((inv) => {
                                                        const key = String(inv.purchase_ledger_id);
                                                        return (
                                                            <tr key={key}>
                                                                <td className="text-center">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input mt-1"
                                                                        checked={!!checkedInvoices[key]}
                                                                        onChange={() =>
                                                                            toggleInvoice(inv.purchase_ledger_id, inv.balance)
                                                                        }
                                                                    />
                                                                </td>
                                                                <td>{getPoLabel(inv)}</td>
                                                                <td>{inv.bill_no || inv.reference_no || inv.job_ref}</td>
                                                                <td>{inv.raised_on}</td>
                                                                <td className="text-end">{inv.balance}</td>
                                                                <td className="text-end">
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        className="form-control form-control-sm text-end"
                                                                        disabled={!checkedInvoices[key]}
                                                                        value={allocAmounts[key] ?? ''}
                                                                        onChange={(e) =>
                                                                            handleAmountChange(
                                                                                inv.purchase_ledger_id,
                                                                                e.target.value,
                                                                                inv.balance
                                                                            )
                                                                        }
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    <div className="supplier-credit-allocation-footer modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading || submitting || credits.length === 0 || !selectedCreditId}
                        >
                            {submitting ? 'Saving...' : 'Allocate'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default SupplierCreditAllocation;
